import { MessageBar, MessageBarBody } from '@fluentui/react-components';
import cn from 'classnames';
import React, { FC, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { ListEmptyPlaceholder } from '../List';
import { Paginator } from '../Pagination/Paginator';
import { ThemeMode, useProviderTheme } from '../hooks';
import { ConversionErrorType } from './CSVConverter/CSVConverterProvider.enums';
import { MetaDataError } from './CSVConverter/CSVConverterProvider.interfaces';
import {
  CourseMapping,
  CourseMappingExportModel,
  CourseMappingUpdateModel,
  CourseTimetableMappingComponentProps,
  ImportIssue,
  ImportIssueKind,
} from './CourseMapping.interfaces';
import './CourseMappingComponent.scss';
import { buildAssignmentExport, resolveImportedMappings, selectChangedMappings } from './CourseMapping.utils';
import { CourseMappingDialogsComponent } from './CourseMappingConfirmationComponent/CourseMappingConfirmationComponent';
import { CourseMappingHeaderComponent } from './CourseMappingHeaderComponent/CourseMappingHeaderComponent';
import {
  importMappings,
  resetMappings,
  setAllowMultipleAzureGroupUsage,
  setCloudCourses,
  setErrorMessage,
  setHasChanges,
  SetInitialMappings,
  setImportIssues,
  setItemsPerPage,
  setMappingFilter,
  setPage,
  setTimetableCourses,
  updateSingleMapping,
} from './CourseMappingRedux/CourseMapping.actions';
import {
  courseMappingReducer,
  getCloudCourses,
  getCourseMappings,
  getEntriesPerPage,
  getError,
  getHasChanges,
  getImportIssues,
  getInitialMappings,
  getMappingsFilter,
  getMaxPage,
  getPage,
  getVisibleItems,
} from './CourseMappingRedux/CourseMapping.reducer';
import { createInitialState } from './CourseMappingRedux/CourseMapping.state';
import { CourseSetComponent } from './CourseSetComponent/CourseSetComponent';
import { IssueListComponent } from './IssueListComponent/IssueListComponent';

export const CourseTimetableMappingComponent: FC<CourseTimetableMappingComponentProps> = (
  props,
) => {
  const theme = useProviderTheme();
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(courseMappingReducer, createInitialState());

  useEffect(() => {
    dispatch(setAllowMultipleAzureGroupUsage(props.allowMultipleAzureGroupUsage ?? false));
    dispatch(setCloudCourses(props.cloudCourseSelection));
    dispatch(setTimetableCourses(props.timetableCourseSelection));
    dispatch(SetInitialMappings(props.assignments));
    dispatch(setMappingFilter(getMappingsFilter(state)));
  }, [
    props.assignments,
    props.timetableCourseSelection,
    props.cloudCourseSelection,
    props.allowMultipleAzureGroupUsage,
  ]);

  const cloudCourseSelection = getCloudCourses(state);
  const timetableCourses = props.timetableCourseSelection;

  const update = props.update;
  //All assignments to work on locally, so props assignments remain untouched;
  const courseMappingsPoolToWorkOn = getCourseMappings(state);
  //Visible assignments filtering or pagination
  const visibleAssignments = getVisibleItems(state);
  const importIssues = getImportIssues(state);

  const handleSave = () => {
    dispatch(setHasChanges(false));
    if (courseMappingsPoolToWorkOn) {
      update?.(
        selectChangedMappings(
          courseMappingsPoolToWorkOn,
          getInitialMappings(state) ?? [],
        ).map<CourseMappingUpdateModel>((mapping) => ({
          timetableCourse: mapping.timetableCourse,
          azureGroupId: mapping.azureGroupId,
          azureGroupName: mapping.azureGroupName,
        })),
      );
    }
  };

  const handleUpdate = (updatedAssignment: CourseMapping) => {
    dispatch(updateSingleMapping(updatedAssignment));
  };

  const handleReset = () => {
    dispatch(resetMappings());
  };

  const handleImport = (rows: CourseMappingExportModel[]) => {
    dispatch(setErrorMessage(null));
    const { updates, issues } = resolveImportedMappings(
      rows,
      timetableCourses,
      cloudCourseSelection ?? [],
    );
    dispatch(importMappings(updates));
    dispatch(setImportIssues(issues));
  };

  const fileErrorText = (type: ConversionErrorType): string => {
    switch (type) {
      case ConversionErrorType.DelimiterError:
        return t('settings:courseimport:courseMappingComponent:error_wrong_delimiter', {
          defaultValue:
            'Ein falsches Trennzeichen wurde verwendet. Bitte verwenden Sie ein Semikolon als Trennzeichen für die CSV-Datei.',
        });
      default:
        return t('settings:courseimport:courseMappingComponent:error_unexpected_csv', {
          defaultValue: 'Beim Hochladen der CSV-Datei ist ein unerwarteter Fehler aufgetreten.',
        });
    }
  };

  const importError = getError(state);

  const issueText = (kind: ImportIssueKind): string => {
    switch (kind) {
      case 'unknownLesson':
        return t('settings:courseimport:courseMappingComponent:issue_unknown_lesson', {
          defaultValue: 'Stundenplankurs nicht gefunden',
        });
      case 'ambiguousGroup':
        return t('settings:courseimport:courseMappingComponent:issue_ambiguous_group', {
          defaultValue: 'Gruppenname ist nicht eindeutig',
        });
      case 'groupNotFound':
      default:
        return t('settings:courseimport:courseMappingComponent:issue_group_not_found', {
          defaultValue: 'Gruppe wurde nicht gefunden',
        });
    }
  };

  // Every group that some other row already holds. Passed down so the pick-list
  // can mark them: assigning one twice is rejected by the backend unless the
  // organization allows shared groups (Bug #11517).
  const appliedAzureGroupIds: { groupId: string; revisionsId: string }[] = [];
  (courseMappingsPoolToWorkOn ?? []).forEach((assignment) => {
    if (assignment.azureGroupId) {
      appliedAzureGroupIds.push({
        groupId: assignment.azureGroupId,
        revisionsId: assignment.timetableCourse.entityRevisionsId,
      });
    }
  });

  const mappingItems = visibleAssignments?.map((assignment) => (
    <CourseSetComponent
      assignment={assignment}
      cloudCourseSelection={cloudCourseSelection ?? []}
      key={assignment.timetableCourse.entityRevisionsId}
      update={handleUpdate}
      error={assignment.error}
      azureGroupIdsInUseElsewhere={
        props.allowMultipleAzureGroupUsage
          ? undefined
          : new Set(
              appliedAzureGroupIds
                .filter(
                  (applied) =>
                    applied.revisionsId !== assignment.timetableCourse.entityRevisionsId,
                )
                .map((applied) => applied.groupId),
            )
      }
    />
  ));

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <MessageBar intent="warning" className="CourseMappingComponent__activation-hint">
        <MessageBarBody>
          {t('settings:courseimport:courseMappingComponent:changes_activation_delay_hint', {
            defaultValue:
              'Hier gespeicherte Änderungen werden nicht sofort übernommen. Es kann einige Minuten dauern, bis sie wirksam werden.',
          })}
        </MessageBarBody>
      </MessageBar>
      <CourseMappingHeaderComponent
        filterChanged={(filter) => dispatch(setMappingFilter(filter))}
        transfer={{
          csvAssignmentsExportData: buildAssignmentExport(courseMappingsPoolToWorkOn ?? []),
          cloudCourseSelection: cloudCourseSelection ?? [],
          timetableCourseSelection: timetableCourses,
          dataImported: handleImport,
          errorOccured: (error: MetaDataError) =>
            dispatch(setErrorMessage(fileErrorText(error.type))),
        }}
      />
      <div className="flex flex-col gap-1 CourseMappingComponent__content">
        {importError && <IssueListComponent errorText={importError} listData={[]} />}
        {importIssues.length > 0
          && (['unknownLesson', 'groupNotFound', 'ambiguousGroup'] as ImportIssueKind[])
            .map((kind) => ({
              kind,
              items: importIssues.filter((issue: ImportIssue) => issue.kind === kind),
            }))
            .filter((group) => group.items.length > 0)
            .map((group) => (
              <IssueListComponent
                key={group.kind}
                errorText={issueText(group.kind)}
                listData={group.items.map((issue) =>
                  issue.value ? `${issue.timetableCourse} → ${issue.value}` : issue.timetableCourse,
                )}
              />
            ))}
        {mappingItems}
        {mappingItems?.length === 0 && <ListEmptyPlaceholder />}
      </div>
      <div
        className={cn('flex gap-4 CourseMappingComponent__wrap-pagination', {
          'CourseMappingComponent__wrap-pagination--dark': theme.mode === ThemeMode.Dark,
        })}
      >
        <Paginator
          currentPage={getPage(state)}
          entriesPerPageSteps={10}
          maxEntriesPerPage={100}
          maxPage={getMaxPage(state)}
          entriesPerPageDefaultValue={getEntriesPerPage(state)}
          entriesPerPageChanged={(entriesPerPage) => dispatch(setItemsPerPage(entriesPerPage))}
          pageChanged={(page) => dispatch(setPage(page))}
        />
        <div className="flex flex-1 justify-end">
          <CourseMappingDialogsComponent
            disabled={!getHasChanges(state)}
            hasError={courseMappingsPoolToWorkOn?.some(
              (assignment) => assignment.error && assignment.error > 0,
            )}
            changes={selectChangedMappings(
              courseMappingsPoolToWorkOn ?? [],
              getInitialMappings(state) ?? [],
            )}
            reset={handleReset}
            save={handleSave}
          />
        </div>
      </div>
    </div>
  );
};
